import React, { useCallback, useMemo } from 'react';
import PaymentReportsFilters from './components/PaymentReportsFilters';
import PaymentReportsTable from './components/PaymentReportsTable';
import usePaymentReportsData from './hooks/usePaymentReportsData';
import { useLanguage } from '../../../contexts/LanguageContext';
import { formatDateForApi } from '../../../utils/dateUtils';
import PaymentReceiptPdfForm from './utils/PaymentReceiptPdfForm';
import GenericFileExportButton from '../../../components/common/GenericFileExportButton';
import { useLazyExportPaymentReportsQuery } from '../../../api/endpoints/creditCardPayment';

const PaymentReportsPage = () => {
    const { th, language } = useLanguage();
    const {
        rows,
        loading,
        totalCount,
        pagination,
        sortField,
        sortOrder,
        onPageChange,
        onSort,
        filters,
        handleApplyFilters,
        handleResetFilters,
        downloadReceipt,
        isLoadingReceipt
    } = usePaymentReportsData();

    const exportParams = useMemo(() => ({
        startDate: formatDateForApi(filters.startDate),
        endDate: formatDateForApi(filters.endDate),
        searchText: filters.searchText || null,
        sortField: sortField,
        sortOrder: sortOrder === 1 ? 'asc' : 'desc',
        compressionType: 0
    }), [filters, sortField, sortOrder]);

    // Makbuz indirme handler
    const handleDownloadReceipt = useCallback(async (transactionId, rowData) => {
        const receiptData = await downloadReceipt(transactionId);
        
        if (receiptData) {
            // PDF için çevirileri hazırla
            const receiptTranslations = {
                title: th('paymentReports.receipt.title'),
                customerName: th('paymentReports.receipt.customerName'),
                customerNo: th('paymentReports.receipt.customerNo'),
                paymentType: th('paymentReports.receipt.paymentType'),
                collectionAmount: th('paymentReports.receipt.collectionAmount'),
                transactionDate: th('paymentReports.receipt.transactionDate'),
                bankApprovalCode: th('paymentReports.receipt.bankApprovalCode'),
                bank: th('paymentReports.receipt.bank'),
                address: th('paymentReports.receipt.address'),
                phone: th('paymentReports.receipt.phone'),
                fax: th('paymentReports.receipt.fax'),
                legalTextPart1: th('paymentReports.receipt.legalTextPart1'),
                legalTextPart2: th('paymentReports.receipt.legalTextPart2'),
                inWords: th('paymentReports.receipt.inWords'),
                refundFormTitle: th('paymentReports.receipt.refundFormTitle'),
                refundFormText: th('paymentReports.receipt.refundFormText'),
                phoneNo: th('paymentReports.receipt.phoneNo'),
                authorizedSignature: th('paymentReports.receipt.authorizedSignature')
            };

            // PDF oluştur ve indir
            const pdfForm = new PaymentReceiptPdfForm();
            const pdfDoc = pdfForm.generate(receiptData, receiptTranslations, language);
            
            // Dosya adı: Makbuz_BayiKodu_TransactionId.pdf
            const fileName = `Makbuz_${rowData.dealerCode || 'unknown'}_${transactionId}.pdf`;
            pdfDoc.download(fileName);
        }
    }, [downloadReceipt, th, language]);

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">
                    {th('paymentReports.pageTitle') || 'Ödeme Raporları'}
                </h1>

                <GenericFileExportButton
                    useExportQuery={useLazyExportPaymentReportsQuery}
                    exportParams={exportParams}
                    buttonLabel={th('paymentReports.excelExport')}
                    buttonClassName="p-button-success"
                    icon="pi pi-file-excel"
                    dataExtractor={(result) => result?.fileBase64}
                    contentTypeExtractor={(result) => result?.contentType}
                />
            </div>

            <div className="mb-2">
                <PaymentReportsFilters
                    filters={filters}
                    onApplyFilters={handleApplyFilters}
                    onReset={handleResetFilters}
                />
            </div>

            <PaymentReportsTable
                rows={rows}
                loading={loading}
                totalCount={totalCount}
                pagination={pagination}
                sortField={sortField}
                sortOrder={sortOrder}
                onPageChange={onPageChange}
                onSort={onSort}
                onDownloadReceipt={handleDownloadReceipt}
                isLoadingReceipt={isLoadingReceipt}
            />
        </div>
    );
};

export default PaymentReportsPage;
